import { UseFormValues } from "@formiz/core/dist/types/form.types";
import { Button, ButtonVariant, Icon, IconSize, IconVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { ButtonGroup, Image } from "@nextui-org/react";
import { useCallback, useRef, useState } from "react";

import AvatarGeneratorModal from "@/components/AvatarGeneratorModal";
import useAvatarUpload from "@/components/CharacterForm/useAvatarUpload";
import { Grid } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { AvatarPreset, FormTypes } from "@/types";
import { cn, getAvatarUrl } from "@/utils";

const AVATAR_SIZE_LARGE = 400;
const AVATAR_SIZE_MEDIUM = 200;
const AVATAR_SIZE_DEFAULT = 256;

type AvatarCreateProps = {
  characterAvatar: string;
  setCharacterAvatar: (avatar: string) => void;
  disabled: boolean;
  persona?: string;
  form?: UseFormValues;
  formType?: FormTypes;
  classNames?: {
    image?: string;
  };
  isAiPromptEnabled?: boolean;
  id?: string;
  isUploadAllowed?: boolean;
};

//[ ] TODO: Change for <AvatarWithModal>
const AvatarCreate = ({
  characterAvatar,
  setCharacterAvatar,
  disabled,
  persona,
  form,
  formType,
  classNames,
  isAiPromptEnabled = true,
  id = "image-picker",
  isUploadAllowed = true,
}: AvatarCreateProps) => {
  const [stateIsAvatarModalVisible, setStateIsAvatarModalVisible] = useState(false);
  const { isDesktop } = useMedia();
  const refImagePicker = useRef<HTMLInputElement>(null);
  const { onImageUpload, isImageUploading } = useAvatarUpload({ setCharacterAvatar, imagePickerRef: refImagePicker });

  const personaAvatarSize = isDesktop ? AVATAR_SIZE_LARGE : AVATAR_SIZE_MEDIUM;
  const avatarSize = persona ? personaAvatarSize : AVATAR_SIZE_DEFAULT;

  const onCloseHandler = useCallback(() => {
    setStateIsAvatarModalVisible(false);
  }, []);

  const onSaveHandler = useCallback(
    (key: string) => {
      if (refImagePicker.current) {
        refImagePicker.current.value = "";
      }
      setCharacterAvatar(key);
      setStateIsAvatarModalVisible(false);
    },
    [setCharacterAvatar],
  );

  const handleGenerateAvatar = useCallback(() => {
    setStateIsAvatarModalVisible(true);
  }, []);

  return (
    <div className="flex flex-col gap-2" data-testid="AvatarCreate">
      {characterAvatar ? (
        <Grid>
          <Image
            className={cn("object-cover", classNames?.image)}
            src={
              getAvatarUrl(characterAvatar, { preset: persona && isDesktop ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_256X256 }) as string
            }
            width={avatarSize}
            height={avatarSize}
          />
        </Grid>
      ) : (
        persona && (
          <div className="bg-zinc-300 text-gray-600 p-12 rounded-2xl size-full">
            <Icon icon={IconVariants.USER} size={IconSize.SMALL} />
          </div>
        )
      )}
      <div className="flex flex-row gap-2">
        <Button
          iconStart={IconVariants.SPARKLES}
          variant={ButtonVariant.SECONDARY}
          disabled={disabled}
          onClick={handleGenerateAvatar}
          aria-label="generate-avatar"
        >
          <Trans i18nKey="common:generate" />
        </Button>
        {isUploadAllowed ? (
          <ButtonGroup size="sm" isDisabled={disabled} className={persona ? "grow *:w-full" : undefined}>
            <input id={id} type="file" accept="image/*" hidden ref={refImagePicker} data-testid="AvatarCreate-UploadInput" />
            <Button
              data-testid="AvatarCreate-UploadButton"
              disabled={disabled}
              iconStart={IconVariants.UPLOAD}
              variant={ButtonVariant.OUTLINED}
              onClick={onImageUpload}
              loading={isImageUploading}
            >
              <Trans i18nKey="common:upload" />
            </Button>
          </ButtonGroup>
        ) : null}
      </div>
      <AvatarGeneratorModal
        currentAvatarUrl={getAvatarUrl(characterAvatar, { preset: AvatarPreset.AVATAR_256X256 }) as string}
        isVisible={stateIsAvatarModalVisible}
        onClose={onCloseHandler}
        onSave={onSaveHandler}
        form={form}
        formType={formType}
        isAiPromptEnabled={isAiPromptEnabled}
      />
    </div>
  );
};

export default AvatarCreate;
